import React, { useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { ModuleDefinition } from "../types";
import { ModuleCatalog } from "../useModuleCatalog";
import { Placement } from "./blueprint";
import { appOf, moduleColor, moduleIcon } from "../moduleStyle";

interface ModulePickerProps {
    placement: Placement | null;
    catalog: ModuleCatalog;
    /** True when the new module would be the scenario's first. */
    isFirst: boolean;
    onPick: (definition: ModuleDefinition) => void;
    onClose: () => void;
}

/** Which modules fit where: triggers only first, error directives only in error routes. */
const allowed = (definition: ModuleDefinition, placement: Placement, isFirst: boolean): boolean => {
    const trigger = definition.kind === 'trigger' || definition.kind === 'instant';
    if (definition.kind === 'directive') {
        return placement.kind === 'onerror';
    }
    if (trigger) {
        return isFirst;
    }

    return true;
};

const ModulePicker = ({ placement, catalog, isFirst, onPick, onClose }: ModulePickerProps) => {
    const { t } = useTranslation(['workflow']);
    const [query, setQuery] = useState('');

    const groups = useMemo(() => {
        if (!placement) {
            return [];
        }
        const q = query.trim().toLowerCase();
        const fits = catalog.modules
            .filter((d) => allowed(d, placement, isFirst))
            .filter((d) => !q || d.label.toLowerCase().includes(q) || d.key.toLowerCase().includes(q) || d.description.toLowerCase().includes(q));
        const byApp: Record<string, ModuleDefinition[]> = {};
        fits.forEach((d) => {
            (byApp[appOf(d.key)] = byApp[appOf(d.key)] || []).push(d);
        });

        return Object.entries(byApp);
    }, [catalog, placement, isFirst, query]);

    return (
        <Modal show={placement !== null} onHide={onClose} size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>{t('workflow:addModule')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <input autoFocus className="form-control mb-3" placeholder={t('workflow:searchModules')} value={query} onChange={(e) => setQuery(e.target.value)} />
                {groups.length === 0 && <div className="text-muted">{t('workflow:noModules')}</div>}
                {groups.map(([app, list]) => (
                    <div key={app} className="mb-3">
                        <div className="text-uppercase small text-muted mb-2">{t('workflow:app_' + app, { defaultValue: app })}</div>
                        <div className="row g-2">
                            {list.map((d) => (
                                <div key={d.key} className="col-md-6">
                                    <button type="button" className="btn btn-light w-100 text-start d-flex gap-2 align-items-center h-100" onClick={() => onPick(d)}>
                                        <span className="rounded-circle d-inline-flex flex-shrink-0 align-items-center justify-content-center text-white" style={{ width: 36, height: 36, background: moduleColor(d.key) }}>
                                            <i className={moduleIcon(d.icon)} />
                                        </span>
                                        <span className="mw-0">
                                            <span className="d-block fw-semibold text-truncate">{d.label}</span>
                                            <span className="d-block small text-muted text-truncate">{d.description}</span>
                                        </span>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </Modal.Body>
        </Modal>
    );
};

export default ModulePicker;
